<template>
  <aha-chat>
    <aha-chat-toggle
      @toggleChatBox="onToggleChatBox"
      :chatBoxVisible="isOpenChatBox"
      :hasChatNotification="hasChatNotification"
      :isDarkTheme="isTextColourLight"
      :isWhiteThemeWithImage="hasBackgroundImage"
      :hasPillWrapper="true"
    >
      <aha-chat-box
        @sendMessage="handleSendMessageFromAudience"
        @retrySendingMessage="retry"
        :chatMessages="chatMessages"
        :isSendingMessage="isSendingMessage"
        :filteringProfanity="!!presentation.filteringProfanity"
        :isDarkTheme="isTextColourLight"
        width="380px"
        chatsHeight="200px"
        :emptyContentHTML="emptyContentHTML"
        :userId="audienceId"
        class="chat-box__custom"
      />
    </aha-chat-toggle>
  </aha-chat>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import Socket from '@/socket'
import colorUtils from 'storybook-utils/colors'

export default {
  props: {
    openByDefault: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState({
      presentation: (state) => state.presentation,
      audienceId: (state) => state.audienceId,
      audienceName: (state) => state.audienceName,
      audienceTeam: (state) => {
        return state.presentation.teamData ?
          state.presentation.teamData.find((team) => team.id === state.audienceQuizTeam) : {}
      },
    }),
    ...mapGetters({
      slideActive: 'activeActivity',
    }),
    ...mapGetters('presentationChat', ['chatMessages', 'isSendingMessage', 'isOpenChatBox', 'hasChatNotification']),
    ...mapGetters('themeModule', ['isTextColourLight']),

    emptyContentHTML() {
      return `
        <p class="chat-box__empty-heading">${this.$t('startTheConversation')}</p>
        <p class="chat-box__empty-text">${this.$t('noChatMessagesYet')}</br>${this.$t('typeSomethingToSayHi')}</p>
      `
    },

    hasBackgroundImage() {
      return this.slideActive.backgroundImage && this.slideActive.backgroundImage.length !== 0
    },

    teamName() {
      return this.audienceTeam ? this.audienceTeam.name : null
    },
  },

  mounted() {
    this.subscribeChatRoomAction({
      presentation: this.presentation,
      audienceId: this.audienceId,
      audienceName: this.audienceName,
      newMessageHandler: this.handleGetMessages,
    })
    this.toggleChatBoxAction({
      isOpen: this.openByDefault,
    })
  },

  destroyed() {
    this.unsubscribeChatRoomAction({
      presentation: this.presentation,
    })
  },

  methods: {
    ...mapActions('presentationChat', [
      'subscribeChatRoomAction',
      'unsubscribeChatRoomAction',
      'sendMessageAction',
      'toggleChatBoxAction',
      'toggleFocusChatInputAction',
      'showChatNotificationAction',
    ]),

    handleGetMessages(newMessage) {
      if (newMessage.uniqueAccessCode && !this.isOpenChatBox) {
        this.showChatNotificationAction({
          isShowing: true,
        })
      }
    },

    retry(message) {
      this.handleSendMessageFromAudience(message)
    },

    handleSendMessageFromAudience(message) {
      const {
        audienceName: name,
        audienceEmoji: avatar,
      } = this.$store.state

      const payload = {
        uniqueAccessCode: this.presentation.uniqueAccessCode,
        presentationId: this.presentation.id,
        name,
        userId: this.audienceId,
        avatar,
        message,
        type: 'text',
        role: 'audience',
        socketId: Socket.getSocketId(),
        timestamp: Date.now(),
        color: colorUtils.getAudienceColor(this.audienceId),
        teamName: this.teamName,
      }

      this.sendMessageAction({ payload })
    },

    onToggleChatBox() {
      this.toggleChatBoxAction()
      if (this.isOpenChatBox && this.hasChatNotification) {
        this.showChatNotificationAction({
          isShowing: false,
        })
      }
    },

    onFocusChatInput() {
      this.toggleFocusChatInputAction({
        isFocus: true,
      })
    },

    onBlurChatInput() {
      this.toggleFocusChatInputAction({
        isFocus: false,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.chat-box__custom {
  position: absolute;
  right: 0;
  bottom: 3rem;
  box-shadow: 0px 6px 11px rgba(0, 0, 0, 0.07);
  backdrop-filter: blur(6px);
  & ::v-deep {
    .chat-box__header {
      top: -1px;
    }
    .chat-message {
      .__avatar {
        margin-right: 8px;
      }

      & .chat-message.__name {
        & span:first-child {
          display: inline-block;
          max-width: 200px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          vertical-align: middle;
        }
      }
    }
    .__user-type-icon {
      margin-bottom: -2px;
    }
  }
  @media (max-width: 767px) {
    right: -0.5rem;
    width: 288px !important;
    & ::v-deep {
      .chat-box__empty {
        width: 90%;
      }
    }
  }
}
</style>
